import { ENTITY_COLORS, ENTITY_LABELS, ENTITY_ICONS } from './graph-viewer-data.util';
import { EntityType, ViewerGraphData, ViewerNode } from '../models/graph-viewer.models';
import {
  getAggregateItems,
  getNodeSearchableValues,
  isActionableEmailValue,
  isAggregateHubNode,
} from '../../../../services/link-analysis/link-analysis-searchable.util';
import {
  collectMergedPlatformDisplayFields,
  displayLabelForFieldKey,
} from '../../../../utils/canonical-field-key';

export interface NodeDetailField {
  label: string;
  value: string;
  kind?: 'text' | 'link' | 'badge' | 'chips' | 'longtext' | 'account' | 'source';
  href?: string;
  tone?: 'default' | 'success' | 'warning' | 'danger' | 'muted' | 'info';
  chips?: string[];
  subValue?: string;
  /** When set, detail card shows a search icon for this value. */
  searchQueryType?: 'phone' | 'email' | 'username' | 'name' | 'image';
  /**
   * Aggregate row jump (R12): click opens this source node and preselects `value`.
   * Never run OSINT/Leak/Face from the aggregate itself.
   */
  jumpToNodeId?: string;
  preselectValueType?: 'phone' | 'email' | 'username' | 'name' | 'image';
}

export interface NodeDetailStat {
  label: string;
  value: string;
  tone?: NodeDetailField['tone'];
}

export interface NodeDetailSection {
  title: string;
  icon?: string;
  fields: NodeDetailField[];
  layout?: 'default' | 'cards' | 'compact';
}

export interface NodeDetailImage {
  url: string;
  label: string;
}

export interface NodeDetailPanel {
  summary: string;
  stats: NodeDetailStat[];
  sections: NodeDetailSection[];
  images: NodeDetailImage[];
}

export interface EnrichedConnection {
  id: string;
  relationship: string;
  relationshipType: string;
  node: ViewerNode;
  confidence?: number;
  evidenceTier?: string;
  role?: string;
  direction: 'out' | 'in';
}

export interface ConnectionGroup {
  relationship: string;
  items: EnrichedConnection[];
}

function humanizeKey(key: string): string {
  return key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Same label formatting as OSINT Search `toDisplayName`. */
function toOsintDisplayName(value: string): string {
  const key = String(value || '')
    .trim()
    .toLowerCase();
  if (key === 'mobile-pan' || key === 'mobile_pan' || key === 'mobilepan') {
    return 'Pan detail';
  }
  if (key === 'iocl-subsidy' || key === 'iocl_subsidy' || key === 'ioclsubsidy') {
    return 'IndianOil LPG';
  }
  if (key === 'num2google' || key === 'num2-google' || key === 'num2_google') {
    return 'Google';
  }
  return String(value || '')
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ') || 'Unknown';
}

function humanizeRelationship(value: string): string {
  return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function shouldHideConnectionRelationship(relationshipType: string, relationship: string): boolean {
  const type = relationshipType.toLowerCase().replace(/-/g, '_');
  if (type === 'found_in' || type === 'found_in_source') return true;
  const label = relationship.trim().toLowerCase();
  return label === 'found in' || label === 'found in source';
}

function formatTier(tier: string | undefined): string | undefined {
  if (!tier) return undefined;
  const match = tier.match(/tier\s*(\d+)/i);
  return match ? `Tier ${match[1]}` : humanizeKey(tier);
}

function formatConfidence(value: unknown): string | undefined {
  if (value == null || value === '') return undefined;
  const num = Number(value);
  if (!Number.isFinite(num)) return String(value);
  const pct = num <= 1 ? Math.round(num * 100) : Math.round(num);
  return `${pct}%`;
}

function formatRole(role: string | undefined): string | undefined {
  if (!role) return undefined;
  return humanizeKey(role);
}

function formatSourceType(sourceType: string | undefined): string | undefined {
  if (!sourceType) return undefined;
  return humanizeKey(sourceType.replace(/_/g, ' '));
}

const IMAGE_FIELD_KEYS = new Set([
  'picture',
  'picture_url',
  'image_url',
  'imageurl',
  'image_hash',
  'previewimageurl',
  'sourceimageurl',
  'thumbnail',
  'photo',
  'photo_url',
  'avatar',
  'avatar_url',
  'profile_photo',
  'profile_photo_url',
  'cover_url',
  'banner_url',
]);

/** Auth-gated OSINT image proxy: relative `/api/...` or absolute `http(s)://.../api/...`. */
function isOsintImageProxyUrl(value: unknown): boolean {
  return /\/api\/osint\/image\//i.test(String(value || '').trim());
}

const LOGO_FIELD_KEYS = new Set([
  'logo',
  'logo_url',
  'log_url',
  'site_logo',
  'brand_logo',
  'favicon',
  'favicon_url',
  'icon',
  'icon_url',
]);

const IMAGE_URL_PATTERN = /\.(jpe?g|png|gif|webp|bmp|svg|avif)(\?|#|$)/i;

function readRawFieldValue(field: unknown): unknown {
  if (field == null) return null;
  if (typeof field === 'string' || typeof field === 'number' || typeof field === 'boolean') {
    return field;
  }
  if (typeof field === 'object' && field !== null && 'value' in field) {
    return (field as { value: unknown }).value;
  }
  return null;
}

function isHttpAssetUrl(value: unknown): value is string {
  const url = String(value || '').trim();
  if (!url) return false;
  // Relative or absolute OSINT image proxy — valid for <img> via gallery Bearer fetch.
  if (isOsintImageProxyUrl(url)) {
    return /^https?:\/\//i.test(url) || url.startsWith('/');
  }
  if (!/^https?:\/\//i.test(url)) return false;
  if (/marker-icon\.png$/i.test(url)) return false;
  if (/leaflet@.*\/images\/marker/i.test(url)) return false;
  return true;
}

function isLikelyImageUrl(value: unknown): value is string {
  const url = String(value || '').trim();
  if (!isHttpAssetUrl(url)) return false;
  if (IMAGE_URL_PATTERN.test(url)) return true;
  return isOsintImageProxyUrl(url);
}

function isLogoFieldKey(key: string): boolean {
  const normalized = key.toLowerCase().replace(/[\s-]/g, '_');
  if (LOGO_FIELD_KEYS.has(normalized)) return true;
  return /(^|_)logo($|_)|favicon|site[_-]?icon|brand[_-]?logo/i.test(normalized);
}

function isLogoUrl(value: unknown): boolean {
  const url = String(value || '').trim().toLowerCase();
  if (!url) return false;
  return (
    /\/logo[s]?(?:\/|\.|-|$)/i.test(url) ||
    /favicon/i.test(url) ||
    /brand[_-]?logo/i.test(url) ||
    /site[_-]?icon/i.test(url)
  );
}

function isBase64DataUrl(value: unknown): boolean {
  const text = String(value || '').trim();
  if (!text) return false;
  return /^data:[^;,\s]+;base64,/i.test(text) || /^data:image\/[a-z0-9.+-]+;base64,/i.test(text);
}

function shouldSkipLogoDetail(fieldKey: string | undefined, value: unknown): boolean {
  if (isBase64DataUrl(value)) return true;
  if (fieldKey && isLogoFieldKey(fieldKey)) return true;
  if (typeof value === 'string' && /^https?:\/\//i.test(value) && isLogoUrl(value)) return true;
  return false;
}

function isImageFieldKey(key: string): boolean {
  if (isLogoFieldKey(key)) return false;
  const normalized = key.toLowerCase().replace(/[\s-]/g, '_');
  if (IMAGE_FIELD_KEYS.has(normalized)) return true;
  return /image|photo|picture|avatar|thumbnail|banner|cover/i.test(key);
}

export function collectNodeDetailImages(node: ViewerNode): NodeDetailImage[] {
  const images = new Map<string, string>();
  const raw = node.raw || {};
  const metadata = raw.metadata || {};

  const addImage = (url: unknown, label: string, trusted = false, allowLogo = false): void => {
    if (!allowLogo && shouldSkipLogoDetail(undefined, url)) return;
    const valid = trusted || allowLogo ? isHttpAssetUrl(url) : isLikelyImageUrl(url);
    if (!valid) return;
    const normalizedUrl = String(url).trim();
    if (!images.has(normalizedUrl)) {
      images.set(normalizedUrl, label.trim() || 'Image');
    }
  };

  const platform = metadata.platform ? humanizeKey(String(metadata.platform)) : String(node.label || 'Source');
  const baseLabel =
    raw.type === 'image' || raw.type === 'face_result' || node.type === 'image'
      ? String(node.label || 'Image')
      : `${platform} photo`;

  // Breach logos should show for leak sources
  if (raw.type === 'leak_source' || metadata.sourceContext === 'leak_source' || metadata.sourceContext === 'drill_down_leak_source') {
    const logo =
      metadata.logo ||
      metadata.previewImageUrl ||
      metadata.imageUrl ||
      raw.imageUrl ||
      metadata.sourceData?.logo ||
      metadata.leakData?.logo;
    addImage(logo, `${platform} logo`, true, true);
  }

  addImage(node.image, baseLabel, true);
  addImage(raw.imageUrl, baseLabel, true);
  addImage(raw.image_url, baseLabel, true);
  addImage(raw.thumbnail, 'Thumbnail', true);
  addImage(metadata.matchImageUrl, 'Face match', true);
  addImage(metadata.display_image_url, 'Face match', true);
  addImage(metadata.previewImageUrl, 'Preview image', true, Boolean(metadata.logo));
  addImage(metadata.sourceImageUrl, 'Source image', true);
  addImage(metadata.imageUrl, baseLabel, true);
  addImage(metadata.image_url, baseLabel, true);
  addImage(metadata.thumbnail, 'Thumbnail', true);
  addImage(metadata.relatedImageUrl, baseLabel, true);
  (metadata.collectedPhotos || []).forEach((photo: any) => {
    addImage(typeof photo === 'string' ? photo : photo?.url, photo?.label || baseLabel, true);
  });
  addImage(metadata.queryValue, baseLabel, raw.type === 'image' || node.type === 'image');

  const faceMatches = Array.isArray(metadata.matches)
    ? metadata.matches
    : Array.isArray(metadata.results)
      ? metadata.results
      : [];
  faceMatches.forEach((match: any, index: number) => {
    addImage(
      match?.display_image_url || match?.image_url,
      `Face match ${index + 1}`,
      true,
    );
  });

  const searchableImages = getNodeSearchableValues({ metadata }).image;
  if (Array.isArray(searchableImages)) {
    searchableImages.forEach((value: unknown, index: number) => {
      addImage(value, `${baseLabel} ${index + 1}`, true);
    });
  }

  if (
    metadata.collectionType === 'image' ||
    metadata.collectionType === 'photo' ||
    isAggregateHubNode({ type: raw.type, metadata }) ||
    raw.type === 'image' ||
    node.type === 'image'
  ) {
    const collected = getAggregateItems({ metadata }).map((item) => item.value);
    if (Array.isArray(collected)) {
      collected.forEach((value: unknown, index: number) => {
        addImage(value, `Collected image ${index + 1}`, true);
      });
    }
  }

  if (raw.type === 'face_result' || node.type === 'image') {
    addImage(metadata.queryValue, String(node.label || 'Face match'), true);
  }

  const sourceFields = metadata.sourceData?.fields;
  if (sourceFields && typeof sourceFields === 'object') {
    Object.entries(sourceFields).forEach(([key, field]) => {
      if (isLogoFieldKey(key)) return;
      const value = readRawFieldValue(field);
      if (isImageFieldKey(key)) {
        addImage(value, humanizeKey(key), true);
      } else if (isLikelyImageUrl(value)) {
        addImage(value, humanizeKey(key));
      }
    });
  }

  const sourceRefs = Array.isArray(raw.sourceReferences) ? raw.sourceReferences : [];
  sourceRefs.forEach((ref: any, index: number) => {
    const label = formatFieldValue(ref?.label) || `Source ${index + 1}`;
    if (!shouldSkipLogoDetail(undefined, ref?.url)) addImage(ref?.url, label, true);
    if (!shouldSkipLogoDetail(undefined, ref?.image_url)) addImage(ref?.image_url, label, true);
    if (!shouldSkipLogoDetail(undefined, ref?.thumbnail)) addImage(ref?.thumbnail, label, true);
  });

  return Array.from(images.entries()).map(([url, label]) => ({ url, label }));
}

function pushField(
  fields: NodeDetailField[],
  label: string,
  value: unknown,
  extra: Partial<NodeDetailField> = {},
  fieldKey?: string,
): void {
  if (value == null || value === '') return;
  if (Array.isArray(value) && !value.length) return;
  if (typeof value === 'object' && !Array.isArray(value)) return;
  if (isBase64DataUrl(value)) return;
  if (shouldSkipLogoDetail(fieldKey ?? label, value)) return;

  if (Array.isArray(value)) {
    const chips = value
      .map((item) => String(item))
      .filter(Boolean)
      .filter((item) => !isBase64DataUrl(item))
      .filter((item) => !shouldSkipLogoDetail(fieldKey ?? label, item));
    if (!chips.length) return;
    fields.push({ label, value: chips.join(', '), kind: 'chips', chips, ...extra });
    return;
  }

  const text = String(value).trim();
  if (!text) return;
  if (isBase64DataUrl(text)) return;
  if (shouldSkipLogoDetail(fieldKey ?? label, text)) return;
  const isUrl = /^https?:\/\//i.test(text);
  const searchQueryType =
    extra.searchQueryType !== undefined
      ? extra.searchQueryType
      : resolveDetailSearchQueryType(fieldKey || label, text);
  fields.push({
    label,
    value: text,
    kind: extra.kind ?? (isUrl ? 'link' : 'text'),
    href: isUrl ? text : extra.href,
    ...extra,
    ...(searchQueryType ? { searchQueryType } : {}),
  });
}

function roleTone(role: string | undefined): NodeDetailField['tone'] {
  switch ((role || '').toLowerCase()) {
    case 'confirmed':
      return 'success';
    case 'candidate':
      return 'warning';
    case 'supporting':
      return 'info';
    default:
      return 'default';
  }
}

function tierTone(tier: string | undefined): NodeDetailField['tone'] {
  if (!tier) return 'default';
  if (/tier\s*1/i.test(tier)) return 'success';
  if (/tier\s*2/i.test(tier)) return 'warning';
  return 'muted';
}

function resolveNodeLabel(nodeMap: Map<string, ViewerNode>, nodeId: string | undefined): string | undefined {
  if (!nodeId) return undefined;
  return nodeMap.get(nodeId)?.label || nodeId.split(':').slice(1).join(':') || nodeId;
}

function formatFieldValue(value: unknown): string | undefined {
  if (value == null || value === '') return undefined;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') {
    const text = value.trim();
    if (!text || text === '[object Object]') return undefined;
    return text;
  }
  return undefined;
}

function extractSourceFieldValue(entry: any): string | undefined {
  if (entry == null) return undefined;
  if (typeof entry === 'object' && 'value' in entry) {
    const type = String(entry.type || '').toLowerCase();
    const value = entry.value;
    if (value == null && type !== 'boolean') return undefined;
    if (type === 'boolean' || typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (type === 'datetime' || type === 'date') {
      const date = new Date(String(value));
      return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
    }
    if (Array.isArray(value)) {
      if (!value.length) return 'N/A';
      return value
        .map((item) => (item && typeof item === 'object' ? formatObjectFieldValue(item) : formatFieldValue(item)))
        .filter(Boolean)
        .join('\n') || 'N/A';
    }
    if (typeof value === 'object') {
      return formatObjectFieldValue(value);
    }
    return formatFieldValue(value);
  }
  if (Array.isArray(entry)) {
    if (!entry.length) return 'N/A';
    return (
      entry
        .map((item) =>
          item && typeof item === 'object'
            ? formatObjectFieldValue(item)
            : formatFieldValue(item),
        )
        .filter(Boolean)
        .join('\n') || 'N/A'
    );
  }
  return formatFieldValue(entry);
}

function formatObjectFieldValue(value: unknown): string | undefined {
  if (value == null) return undefined;
  if (typeof value !== 'object') return formatFieldValue(value);
  if (Array.isArray(value)) {
    const parts = value
      .map((item) =>
        item && typeof item === 'object'
          ? formatObjectFieldValue(item)
          : formatFieldValue(item),
      )
      .filter(Boolean) as string[];
    return parts.length ? parts.join('\n') : undefined;
  }

  try {
    const entries = Object.entries(value as Record<string, unknown>).filter(
      ([, v]) => v !== null && v !== undefined && !(typeof v === 'string' && !v.trim()),
    );
    if (!entries.length) return '{}';
    return entries
      .map(([key, raw]) => {
        const display =
          raw && typeof raw === 'object'
            ? formatObjectFieldValue(raw)
            : formatFieldValue(raw);
        return display ? `${key}: ${display}` : null;
      })
      .filter(Boolean)
      .join('\n');
  } catch {
    return undefined;
  }
}

function flattenDictLikeField(fieldValue: any): Record<string, any> | null {
  if (!fieldValue || typeof fieldValue !== 'object' || !('value' in fieldValue)) return null;
  const type = String(fieldValue.type || '').toLowerCase();
  const nested = fieldValue.value;
  if ((type !== 'dict' && type !== 'object') || !nested || typeof nested !== 'object' || Array.isArray(nested)) {
    return null;
  }

  const flattened: Record<string, any> = {};
  Object.entries(nested).forEach(([key, value]) => {
    if (value == null) return;
    if (typeof value === 'object' && !Array.isArray(value) && 'type' in (value as object) && 'value' in (value as object)) {
      flattened[key] = value;
      return;
    }
    flattened[key] = {
      type: Array.isArray(value) ? 'list' : typeof value === 'object' ? 'object' : typeof value,
      value,
    };
  });
  return flattened;
}

function collectNormalizedOsintFields(sourceFields: Record<string, any>): Array<[string, any]> {
  const normalized: Record<string, any> = {};
  Object.entries(sourceFields).forEach(([fieldName, fieldValue]) => {
    if (String(fieldName).toLowerCase() === 'registered') return;
    const flattened = flattenDictLikeField(fieldValue);
    if (flattened) {
      Object.assign(normalized, flattened);
      return;
    }
    normalized[fieldName] = fieldValue;
  });
  return Object.entries(normalized);
}

function extractNestedOsintValue(value: any): string {
  return extractSourceFieldValue(value) || '';
}

function isMeaningfulOsintAccount(account: any): boolean {
  if (!account || typeof account !== 'object' || Array.isArray(account)) return false;
  return Boolean(
    extractNestedOsintValue(account.name) ||
    extractNestedOsintValue(account.username) ||
    extractNestedOsintValue(account.handle) ||
    extractNestedOsintValue(account.uid) ||
    extractNestedOsintValue(account.profile_url) ||
    extractNestedOsintValue(account.url),
  );
}

function hasInlineOsintAccountData(sourceData: any): boolean {
  if (!sourceData || typeof sourceData !== 'object') return false;
  return Boolean(
    extractNestedOsintValue(sourceData.name) ||
    extractNestedOsintValue(sourceData.username) ||
    extractNestedOsintValue(sourceData.uid) ||
    (Array.isArray(sourceData.names) && sourceData.names.length > 0) ||
    (Array.isArray(sourceData.vpas) && sourceData.vpas.length > 0) ||
    extractNestedOsintValue(sourceData.profile_url) ||
    extractNestedOsintValue(sourceData.maps_url),
  );
}

const PLATFORM_FIELD_SKIP = new Set([
  'groups',
  'group_items',
  'group_years',
  'platform_variables',
  'map',
  'map_data',
  'Map Data',
  'tags',
  'picture',
  'picture_url',
  'image_url',
  'image_hash',
  ...LOGO_FIELD_KEYS,
]);

function extractPlatformFields(sourceData: any): NodeDetailField[] {
  const fields: NodeDetailField[] = [];
  const sourceFields = sourceData?.fields;
  if (!sourceFields || typeof sourceFields !== 'object') return fields;

  Object.entries(sourceFields).forEach(([key, entry]) => {
    if (PLATFORM_FIELD_SKIP.has(key) || isLogoFieldKey(key)) return;
    const value = extractSourceFieldValue(entry);
    if (!value || shouldSkipLogoDetail(key, value)) return;
    if (shouldOmitOsintCardPictureField(key, value)) return;
    const label = humanizeKey(key);
    const isUrl = /^https?:\/\//i.test(value);
    pushField(fields, label, value, { tone: 'default', kind: isUrl ? 'link' : 'text' }, key);
  });

  return fields;
}

function appendOsintAccountFields(fields: NodeDetailField[], account: any, seenAccounts: Set<string>): boolean {
  if (!account || typeof account !== 'object' || Array.isArray(account)) return false;
  const name = extractSourceFieldValue(account.name) || 'N/A';
  const username = extractSourceFieldValue(account.username ?? account.handle) || 'N/A';
  const uid = extractSourceFieldValue(account.uid ?? account.id) || 'N/A';
  const profileUrl =
    extractSourceFieldValue(account.profile_url ?? account.profileUrl ?? account.url ?? account.maps_url) || 'N/A';
  const signature = `${name}|${username}|${uid}|${profileUrl}`;
  if (seenAccounts.has(signature)) return false;
  seenAccounts.add(signature);

  const reservedKeys = new Set([
    'name',
    'username',
    'handle',
    'uid',
    'id',
    'url',
    'profile_url',
    'profileUrl',
    'maps_url',
  ]);

  pushField(fields, 'Name', name, {}, 'name');
  pushField(fields, 'Username', username, {}, 'username');
  pushField(fields, 'ID', uid);
  const isUrl = /^https?:\/\//i.test(profileUrl);
  pushField(fields, 'Profile URL', profileUrl, { kind: isUrl ? 'link' : 'text', href: isUrl ? profileUrl : undefined });

  Object.keys(account).forEach((key) => {
    if (reservedKeys.has(key)) return;
    const value = extractSourceFieldValue(account[key]);
    if (!value) return;
    const extraIsUrl = /^https?:\/\//i.test(value);
    pushField(fields, toOsintDisplayName(key), value, { kind: extraIsUrl ? 'link' : 'text' }, key);
  });
  return true;
}

function extractInlineOsintAccountFields(sourceData: any): NodeDetailField[] {
  const fields: NodeDetailField[] = [];
  let name = extractNestedOsintValue(sourceData?.name);
  if (!name && Array.isArray(sourceData?.names)) {
    name = sourceData.names.map((item: any) => extractNestedOsintValue(item)).filter(Boolean).join(', ');
  }
  let username = extractNestedOsintValue(sourceData?.username);
  if (!username && Array.isArray(sourceData?.vpas)) {
    username = sourceData.vpas.map((item: any) => extractNestedOsintValue(item)).filter(Boolean).join(', ');
  }
  const uid = extractNestedOsintValue(sourceData?.uid) || 'N/A';
  const profileUrl =
    extractNestedOsintValue(sourceData?.profile_url) || extractNestedOsintValue(sourceData?.maps_url) || 'N/A';
  pushField(fields, 'Name', name || 'N/A', {}, 'name');
  pushField(fields, 'Username', username || 'N/A', {}, 'username');
  pushField(fields, 'ID', uid);
  const isUrl = /^https?:\/\//i.test(profileUrl);
  pushField(fields, 'Profile URL', profileUrl, { kind: isUrl ? 'link' : 'text', href: isUrl ? profileUrl : undefined });
  return fields;
}

function extractNoAccountOsintFields(sourceData: any): NodeDetailField[] {
  const fields: NodeDetailField[] = [];
  if (Array.isArray(sourceData?.names) && sourceData.names.length > 0) {
    const names = sourceData.names.map((item: any) => extractNestedOsintValue(item)).filter(Boolean).join(', ');
    pushField(fields, 'Names', names);
  }
  if (Array.isArray(sourceData?.vpas) && sourceData.vpas.length > 0) {
    const vpas = sourceData.vpas.map((item: any) => extractNestedOsintValue(item)).filter(Boolean).join(', ');
    pushField(fields, 'VPAs', vpas);
  }
  const uid = extractNestedOsintValue(sourceData?.uid);
  if (uid) pushField(fields, 'ID', uid);
  if (!fields.length) pushField(fields, 'Status', 'No account found');
  return fields;
}

function collectOsintPlatformCardFields(sourceData: any): NodeDetailField[] {
  if (!sourceData || typeof sourceData !== 'object' || Array.isArray(sourceData)) return [];

  const fields: NodeDetailField[] = [];
  const merged = collectMergedPlatformDisplayFields(sourceData);

  merged.forEach((item) => {
    if (
      PLATFORM_FIELD_SKIP.has(item.key) ||
      PLATFORM_FIELD_SKIP.has(`${item.key}s`) ||
      isLogoFieldKey(item.key)
    ) {
      return;
    }
    if (item.key === 'picture_url' || item.key === 'picture' || item.key === 'image_hash') {
      return;
    }

    const rawForDisplay =
      item.values.length <= 1 && item.rawField != null
        ? item.rawField
        : {
            type: Array.isArray(item.value) ? 'list' : typeof item.value,
            value: item.value,
          };
    const value = extractSourceFieldValue(rawForDisplay);
    const display = value || (item.keepEmptyList ? 'N/A' : '');
    if (!display) return;
    if (shouldOmitOsintCardPictureField(item.key, display)) return;

    const isUrl = /^https?:\/\//i.test(display) && display !== 'N/A';
    let taggedSearchType =
      resolveOsintSearchTypeTag(item.rawField) ||
      (item.searchType === 'phone' ||
      item.searchType === 'email' ||
      item.searchType === 'username' ||
      item.searchType === 'name' ||
      item.searchType === 'image'
        ? item.searchType
        : undefined);
    if (item.key === 'email_hint' || item.searchType === 'email_hint') {
      taggedSearchType = undefined;
    } else if (taggedSearchType === 'email' && !isActionableEmailValue(display)) {
      taggedSearchType = undefined;
    }
    pushField(
      fields,
      item.label || displayLabelForFieldKey(item.key),
      display,
      {
        tone: 'default',
        kind: isUrl ? 'link' : 'text',
        href: isUrl ? display : undefined,
        ...(taggedSearchType ? { searchQueryType: taggedSearchType } : {}),
      },
      item.key,
    );
  });

  if (!fields.length && sourceData.has_account === false) {
    fields.push(...extractNoAccountOsintFields(sourceData));
  }

  if (!fields.length && sourceData.has_account === true) {
    pushField(fields, 'Status', 'Account found');
  }

  return fields;
}

function extractPlatformAccounts(sourceData: any): NodeDetailField[] {
  const accounts = Array.isArray(sourceData?.accounts) ? sourceData.accounts : [];
  const fields: NodeDetailField[] = [];

  accounts.forEach((account: any, index: number) => {
    const name = extractSourceFieldValue(account?.name);
    const username = extractSourceFieldValue(account?.username);
    const profileUrl = extractSourceFieldValue(account?.profile_url || account?.profileUrl || account?.url);
    const uid = extractSourceFieldValue(account?.uid);
    const primary = name || username || profileUrl;
    if (!primary) return;

    const subParts = [username && username !== name ? `Username: ${username}` : '', uid ? `ID: ${uid}` : '']
      .filter(Boolean)
      .join(' · ');

    fields.push({
      label: accounts.length > 1 ? `Account ${index + 1}` : 'Account',
      value: primary,
      subValue: subParts || undefined,
      kind: 'account',
      href: profileUrl && /^https?:\/\//i.test(profileUrl) ? profileUrl : undefined,
    });
  });

  return fields;
}

function extractMapInsights(sourceData: any): NodeDetailField[] {
  const fields: NodeDetailField[] = [];
  const mapEntries = sourceData?.fields?.map?.value ?? sourceData?.fields?.['Map Data']?.value;
  if (!Array.isArray(mapEntries)) return fields;

  mapEntries.slice(0, 3).forEach((entry: any, index: number) => {
    const popup = entry?.popup || {};
    const title = formatFieldValue(popup.title) || `Map activity ${index + 1}`;
    const address = formatFieldValue(popup.address);
    const comment = formatFieldValue(popup.comment);
    const rating = popup?.platform_variables?.rating;
    const ratingMax = popup?.platform_variables?.rating_max;
    const ratingText =
      rating != null && ratingMax != null ? `Rating: ${rating}/${ratingMax}` : rating != null ? `Rating: ${rating}` : '';

    const parts = [address, ratingText].filter(Boolean);
    if (comment) {
      fields.push({
        label: title,
        value: comment,
        kind: 'longtext',
        subValue: parts.join(' · ') || undefined,
        href: formatFieldValue(popup.url),
        tone: 'default',
      });
    } else if (parts.length) {
      pushField(fields, title, parts.join(' · '), {
        kind: popup.url ? 'link' : 'text',
        href: formatFieldValue(popup.url),
      });
    }
  });

  return fields;
}

function buildSummaryNarrative(
  node: ViewerNode,
  raw: any,
  metadata: any,
  nodeMap: Map<string, ViewerNode>,
): string {
  const typeLabel = entityTypeLabel(node.type).toLowerCase();
  const attribution = formatFieldValue(raw.attributionStatus) || formatRole(raw.role) || 'entity';
  const tier = formatTier(raw.evidenceTier);
  const source = formatSourceType(raw.sourceType);
  const parent = resolveNodeLabel(nodeMap, metadata.parentNodeId);
  const action = metadata.actionType ? humanizeKey(String(metadata.actionType)) : '';

  const parts = [
    `${node.label} is a ${attribution.toLowerCase()} ${typeLabel}`,
    tier ? `with ${tier.toLowerCase()} evidence` : '',
    source ? `from ${source}` : '',
  ].filter(Boolean);

  let sentence = parts.join(' ').replace(/\s+/g, ' ').trim() + '.';

  if (action && parent) {
    sentence += ` Discovered through ${action.toLowerCase()} linked from ${parent}.`;
  } else if (parent) {
    sentence += ` Linked from ${parent}.`;
  } else if (metadata.directQueryLinked) {
    sentence += ' Directly connected to the investigation query.';
  }

  if (Array.isArray(metadata.contradictionFlags) && metadata.contradictionFlags.length) {
    sentence += ` Flagged for review: ${metadata.contradictionFlags.map((f: string) => humanizeKey(f).toLowerCase()).join(', ')}.`;
  }

  return sentence;
}

function buildSummaryStats(raw: any, node: ViewerNode, metadata: any): NodeDetailStat[] {
  const stats: NodeDetailStat[] = [];
  const pushStat = (label: string, value: string | undefined, tone?: NodeDetailStat['tone']) => {
    if (!value) return;
    stats.push({ label, value, tone });
  };

  pushStat('Attribution', formatFieldValue(raw.attributionStatus) || formatRole(raw.role), roleTone(raw.role));
  pushStat('Source', formatSourceType(raw.sourceType), 'info');
  if (metadata.layer != null) pushStat('Layer', `Layer ${metadata.layer}`, 'muted');
  if (metadata.platform) pushStat('Platform', humanizeKey(String(metadata.platform)), 'info');
  pushStat('Confidence', formatConfidence(raw.confidence ?? node.confidence), 'success');
  pushStat('Evidence', formatTier(raw.evidenceTier), tierTone(raw.evidenceTier));

  return stats;
}

export function buildNodeDetailPanel(node: ViewerNode, graphData: ViewerGraphData): NodeDetailPanel {
  const raw = node.raw || {};
  const metadata = raw.metadata || {};
  const nodeMap = new Map(graphData.nodes.map((n) => [n.id, n]));
  const sections: NodeDetailSection[] = [];
  const contextSections: NodeDetailSection[] = [];
  const prioritySections: NodeDetailSection[] = [];

  const leakRecord =
    metadata.leakData ||
    (raw.type === 'leak_source' ? metadata.sourceData : null) ||
    (metadata.sourceData?.leak_data ? metadata.sourceData : null);
  if (leakRecord && typeof leakRecord === 'object') {
    const leakMeta: NodeDetailField[] = [];
    pushField(leakMeta, 'Leak source', leakRecord.name || metadata.platform);
    pushField(leakMeta, 'Description', leakRecord.description || metadata.description);
    pushField(leakMeta, 'Leak date', leakRecord.leak_date || metadata.leakDate);
    if (leakMeta.length) {
      prioritySections.push({ title: 'Leak source', icon: '🛡', fields: leakMeta, layout: 'compact' });
    }

    const leakRows = Array.isArray(leakRecord.leak_data) ? leakRecord.leak_data : [];
    leakRows.slice(0, 8).forEach((row: any, index: number) => {
      if (!row || typeof row !== 'object') return;
      const rowFields: NodeDetailField[] = [];
      Object.entries(row).forEach(([key, value]) => {
        pushField(rowFields, humanizeKey(key), value, {}, key);
      });
      if (rowFields.length) {
        prioritySections.push({
          title: leakRows.length > 1 ? `Leak record ${index + 1}` : 'Leak record',
          icon: '📄',
          fields: rowFields,
          layout: 'compact',
        });
      }
    });
  }

  const sourceData = metadata.sourceData;
  const platformFields = extractPlatformFields(sourceData);
  if (platformFields.length) {
    prioritySections.push({ title: 'Platform details', icon: '📋', fields: platformFields, layout: 'compact' });
  }

  const accountFields = extractPlatformAccounts(sourceData);
  if (accountFields.length) sections.push({ title: 'Accounts on platform', icon: '🔗', fields: accountFields });

  const mapFields = extractMapInsights(sourceData);
  if (mapFields.length) sections.push({ title: 'Map & review activity', icon: '🗺', fields: mapFields });

  const timeline = sourceData?.timeline;
  if (timeline) {
    const timelineFields: NodeDetailField[] = [];
    if (timeline.registered_date) pushField(timelineFields, 'Registered', timeline.registered_date);
    if (timeline.last_seen_date) pushField(timelineFields, 'Last seen', timeline.last_seen_date);
    if (timeline.registered === true && !timeline.registered_date) {
      pushField(timelineFields, 'Registration', 'Account registered', { kind: 'badge', tone: 'success' });
    }
    if (timeline.last_seen === true && !timeline.last_seen_date) {
      pushField(timelineFields, 'Activity', 'Recently active', { kind: 'badge', tone: 'info' });
    }
    if (timelineFields.length) sections.push({ title: 'Activity timeline', icon: '🕐', fields: timelineFields });
  }

  const searchable = getNodeSearchableValues({ metadata });
  if (searchable) {
    const searchableFields: NodeDetailField[] = [];
    pushField(searchableFields, 'Phones', searchable.phone, { kind: 'chips', searchQueryType: 'phone' }, 'phone');
    pushField(searchableFields, 'Emails', searchable.email, { kind: 'chips', searchQueryType: 'email' }, 'email');
    pushField(searchableFields, 'Usernames', searchable.username, {
      kind: 'chips',
      searchQueryType: 'username',
    }, 'username');
    pushField(searchableFields, 'Names', searchable.name, {
      kind: 'chips',
      searchQueryType: 'name',
    }, 'name');
    pushField(searchableFields, 'Images', searchable.image, {
      kind: 'chips',
      searchQueryType: 'image',
    }, 'image');
    if (searchableFields.length) {
      sections.push({ title: 'Searchable values', icon: '🔍', fields: searchableFields });
    }
  }

  if (isAggregateHubNode({ id: node.id, type: raw.type, metadata })) {
    const contactKind = resolveAggregateContactKind(node, String(raw.type || ''), metadata);
    const items = getAggregateItems({ metadata });
    if (contactKind && items.length) {
      sections.push({
        title:
          contactKind === 'phone'
            ? 'Collected phones'
            : contactKind === 'email'
              ? 'Collected emails'
              : contactKind === 'name'
                ? 'Collected names'
                : 'Collected photos',
        icon: '📦',
        fields: buildAggregateContactFields(items, contactKind),
        layout: 'compact',
      });
    }
  }

  const locationContact: NodeDetailField[] = [];
  pushField(locationContact, 'Locations', metadata.locations);
  pushField(locationContact, 'Related names', metadata.relatedNames);
  pushField(locationContact, 'Related emails', metadata.relatedEmails);
  pushField(locationContact, 'Organizations', metadata.organizations);
  pushField(locationContact, 'Profile URLs', metadata.urls);
  if (metadata.queryValue) pushField(locationContact, 'Query value', metadata.queryValue);
  if (metadata.mode) pushField(locationContact, 'Analysis mode', humanizeKey(String(metadata.mode)));
  if (metadata.imageKind) pushField(locationContact, 'Image type', humanizeKey(String(metadata.imageKind)));
  if (metadata.imageRole) pushField(locationContact, 'Image role', humanizeKey(String(metadata.imageRole)));
  if (metadata.sourceName) pushField(locationContact, 'Image source', humanizeKey(String(metadata.sourceName)));
  if (metadata.searchedForFace === true) {
    pushField(locationContact, 'Face search', 'Searched for matching face', { kind: 'badge', tone: 'info' });
  }
  if (locationContact.length) sections.push({ title: 'Locations & linked values', icon: '📍', fields: locationContact });

  const hints = metadata.hintValues;
  if (hints && typeof hints === 'object') {
    const hintFields: NodeDetailField[] = [];
    Object.entries(hints).forEach(([key, values]) => {
      if (!Array.isArray(values) || !values.length) return;
      pushField(hintFields, `${humanizeKey(key)} hints`, values, { kind: 'chips' });
    });
    if (hintFields.length) sections.push({ title: 'Recovered hints', icon: '💡', fields: hintFields });
  }

  if (metadata.summary && typeof metadata.summary === 'string') {
    sections.push({
      title: 'AI summary',
      icon: '✦',
      fields: [{ label: 'Summary', value: metadata.summary, kind: 'longtext' }],
    });
  }

  const aiSections = Array.isArray(metadata.sections) ? metadata.sections : [];
  aiSections.slice(0, 3).forEach((section: any, index: number) => {
    const fields: NodeDetailField[] = [];
    pushField(fields, 'Summary', section?.summary, { kind: 'longtext' });
    pushField(fields, 'Confidence', section?.confidence ? humanizeKey(String(section.confidence)) : undefined, {
      kind: 'badge',
      tone: 'info',
    });
    pushField(fields, 'Evidence tier', formatTier(section?.evidenceTier), {
      kind: 'badge',
      tone: tierTone(section?.evidenceTier),
    });
    if (Array.isArray(section?.analysisBullets)) {
      section.analysisBullets.forEach((bullet: any) => {
        if (!bullet?.label) return;
        pushField(fields, bullet.label, bullet.detail || bullet.value);
      });
    }
    if (Array.isArray(section?.sourceReferences)) {
      pushField(fields, 'Sources', section.sourceReferences.map((item: unknown) => String(item)), { kind: 'chips' });
    }
    if (fields.length) {
      sections.push({
        title: section?.sectionTitle || `AI insight ${index + 1}`,
        icon: '✦',
        fields,
      });
    }
  });

  const flags: NodeDetailField[] = [];
  if (Array.isArray(metadata.contradictionFlags) && metadata.contradictionFlags.length) {
    pushField(
      flags,
      'Needs review',
      metadata.contradictionFlags.map((flag: string) => humanizeKey(flag)),
      { tone: 'warning', kind: 'chips' },
    );
  }
  if (metadata.officialSignalPresent === true) {
    pushField(flags, 'Official signal', 'Official signal present', { tone: 'success', kind: 'badge' });
  } else if (metadata.officialSignalPresent === false) {
    pushField(flags, 'Official signal', 'No official signal detected', { tone: 'muted', kind: 'badge' });
  }
  if (flags.length) sections.push({ title: 'Review signals', icon: '⚑', fields: flags });

  const context: NodeDetailField[] = [];
  pushField(context, 'Discovery action', metadata.actionType ? humanizeKey(String(metadata.actionType)) : undefined);
  pushField(context, 'Linked from', resolveNodeLabel(nodeMap, metadata.parentNodeId));
  pushField(context, 'Search type', metadata.drillDownQueryType ? humanizeKey(String(metadata.drillDownQueryType)) : undefined);
  if (metadata.directQueryLinked === true) {
    pushField(context, 'Investigation link', 'Directly tied to root query', { tone: 'success', kind: 'badge' });
  }
  if (metadata.generatedAt) {
    const date = new Date(metadata.generatedAt);
    if (!Number.isNaN(date.getTime())) pushField(context, 'Generated', date.toLocaleString());
  }
  if (context.length) contextSections.push({ title: 'How this was found', icon: '🔎', fields: context, layout: 'compact' });

  return {
    summary: buildSummaryNarrative(node, raw, metadata, nodeMap),
    stats: buildSummaryStats(raw, node, metadata),
    sections: [
      ...contextSections,
      ...prioritySections,
      ...sections,
    ],
    images: collectNodeDetailImages(node),
  };
}

export function buildNodeDetailSections(node: ViewerNode, graphData: ViewerGraphData): NodeDetailSection[] {
  return buildNodeDetailPanel(node, graphData).sections;
}

export function buildEnrichedConnections(
  node: ViewerNode,
  graphData: ViewerGraphData,
): EnrichedConnection[] {
  return graphData.edges
    .filter((edge) => edge.source === node.id || edge.target === node.id)
    .map((edge) => {
      const outgoing = edge.source === node.id;
      const otherId = outgoing ? edge.target : edge.source;
      const otherNode = graphData.nodes.find((n) => n.id === otherId);
      if (!otherNode) return null;

      const raw = edge.raw || {};
      const relationshipType = String(raw.type || edge.label || 'linked');
      const relationship = humanizeRelationship(String(edge.label || raw.label || relationshipType));

      return {
        id: `${edge.source}::${edge.target}::${relationshipType}`,
        relationship,
        relationshipType,
        node: otherNode,
        confidence:
          raw.confidence != null
            ? raw.confidence <= 1
              ? Math.round(raw.confidence * 100)
              : Math.round(raw.confidence)
            : undefined,
        evidenceTier: formatTier(raw.evidenceTier),
        role: formatRole(raw.role),
        direction: outgoing ? 'out' : 'in',
      } as EnrichedConnection;
    })
    .filter((item): item is EnrichedConnection => !!item)
    .filter(
      (item) => !shouldHideConnectionRelationship(item.relationshipType, item.relationship),
    )
    .sort((a, b) => (b.confidence ?? 0) - (a.confidence ?? 0) || a.node.label.localeCompare(b.node.label));
}

export function groupConnections(connections: EnrichedConnection[]): ConnectionGroup[] {
  const groups = new Map<string, EnrichedConnection[]>();
  connections.forEach((item) => {
    const key = item.relationship;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(item);
  });
  return [...groups.entries()]
    .map(([relationship, items]) => ({ relationship, items }))
    .sort((a, b) => b.items.length - a.items.length || a.relationship.localeCompare(b.relationship));
}

export function entityTypeLabel(type: EntityType): string {
  return ENTITY_LABELS[type] || humanizeKey(type);
}

export function entityTypeIcon(type: EntityType): string {
  return ENTITY_ICONS[type] || '•';
}

export type OsintStyleCardKind = 'platform' | 'identity' | 'identity-hub' | 'generic' | 'leak' | 'face';

export interface OsintStyleRelatedItem {
  id: string;
  label: string;
  image?: string;
  subtitle?: string;
}

export interface OsintStyleLeakRecord {
  label: string;
  fields: NodeDetailField[];
}

export interface OsintStyleCard {
  kind: OsintStyleCardKind;
  title: string;
  category: string;
  found: boolean;
  profileUrl?: string;
  heroImage?: NodeDetailImage | null;
  placeholderIcon: string;
  accent: string;
  fields: NodeDetailField[];
  /** Per-row leak_data blocks (Data 1, Data 2, …) — Leak Search style. */
  leakRecords?: OsintStyleLeakRecord[];
  /** Face Search–style match probability (0–1 or 0–100). */
  matchProbability?: number | null;
  /** Face Search context rows (APP NAME, NAME, PHONE, …). */
  faceContexts?: NodeDetailField[];
  /** Ordered image URL fallbacks for the face preview (Face1 → query photo). */
  faceImageCandidates?: string[];
  description?: string;
  relatedItems: OsintStyleRelatedItem[];
  isPhotoHub?: boolean;
}

export const IDENTITY_HUB_ACCENTS: Record<string, string> = {
  name: '#f97316',
  photo: '#3b82f6',
  email: '#14b8a6',
  email_hint: '#a855f7',
};

/** Map a detail field key/value to a Link Analysis expand search type. */
export function resolveDetailSearchQueryType(
  key: string,
  value: string,
): NodeDetailField['searchQueryType'] | undefined {
  const keyL = String(key || '').toLowerCase().replace(/\s+/g, '_');
  const text = String(value || '').trim();
  if (!text || /^n\/?a$/i.test(text) || text === '-' || text.toLowerCase() === 'null') return undefined;

  if (
    keyL.includes('phone') ||
    keyL.includes('mobile') ||
    keyL.includes('telephone') ||
    keyL === 'tel' ||
    keyL === 'msisdn'
  ) {
    const parts = text.split(/[,|;]+/).map((part) => part.trim()).filter(Boolean);
    const hasValid = parts.some((part) => {
      const digits = part.replace(/\D/g, '');
      return digits.length >= 10 && digits.length <= 15;
    });
    return hasValid ? 'phone' : undefined;
  }

  if (keyL.includes('email') || keyL.includes('mail')) {
    const parts = text.split(/[,|;]+/).map((part) => part.trim()).filter(Boolean);
    return parts.some((part) => isActionableEmailValue(part)) ? 'email' : undefined;
  }

  if (
    keyL.includes('username') ||
    keyL === 'handle' ||
    keyL === 'user_name' ||
    keyL === 'screen_name' ||
    keyL === 'vpa'
  ) {
    return text.length >= 2 ? 'username' : undefined;
  }

  if (
    keyL === 'name' ||
    keyL === 'full_name' ||
    keyL === 'fullname_name' ||
    keyL === 'display_name'
  ) {
    return text.length >= 2 ? 'name' : undefined;
  }

  if (
    (keyL.includes('image') || keyL.includes('photo') || keyL.includes('avatar') || keyL === 'picture') &&
    (/^https?:\/\//i.test(text) || isOsintImageProxyUrl(text) || text.startsWith('data:image/'))
  ) {
    return 'image';
  }

  return undefined;
}

/** Build OSINT / leak / face actions available for a detail-card field.
 * When allowedActionTypes is provided (Phase 6 server contract), only those actions are returned.
 */
export function buildDetailFieldSearchActions(
  queryType: NonNullable<NodeDetailField['searchQueryType']>,
  allowedActionTypes?: string[] | null,
): Array<{ key: string; label: string; actionType: string; queryType: string }> {
  const allow =
    allowedActionTypes == null
      ? null
      : new Set(allowedActionTypes.map((action) => String(action)));

  const include = (actionType: string) => !allow || allow.has(actionType);

  if (queryType === 'image') {
    if (!include('face_search')) return [];
    return [
      {
        key: 'image-face',
        label: 'Face search from image',
        actionType: 'face_search',
        queryType: 'image',
      },
    ];
  }

  const actions: Array<{ key: string; label: string; actionType: string; queryType: string }> = [];
  if (include('osint_search')) {
    actions.push({
      key: `${queryType}-osint`,
      label: `OSINT ${queryType} search`,
      actionType: 'osint_search',
      queryType,
    });
  }
  if (queryType !== 'name' && include('leak_search')) {
    actions.push({
      key: `${queryType}-leak`,
      label: `Leak ${queryType} search`,
      actionType: 'leak_search',
      queryType,
    });
  }
  return actions;
}

/** Prefer a single actionable value when a field holds comma-separated entries. */
export function resolveDetailFieldSearchValue(field: NodeDetailField): string | undefined {
  const queryType = field.searchQueryType;
  if (!queryType) return undefined;

  const candidates =
    field.kind === 'chips' && Array.isArray(field.chips) && field.chips.length
      ? field.chips.map((chip) => String(chip || '').trim()).filter(Boolean)
      : String(field.value || '')
        .split(/[,|;]+/)
        .map((part) => part.trim())
        .filter(Boolean);

  if (!candidates.length) return undefined;

  if (queryType === 'phone' || queryType === 'email' || queryType === 'image') {
    for (const part of candidates) {
      if (resolveDetailSearchQueryType(queryType, part)) return part;
    }
    return undefined;
  }

  return candidates[0];
}

export function isLeakSourceNode(node: ViewerNode | null | undefined): boolean {
  if (!node) return false;
  const raw = node.raw || {};
  const metadata = raw.metadata || {};
  const type = String(raw.type || node.type || '').toLowerCase();
  if (type === 'leak_source') return true;
  const context = String(metadata.sourceContext || '').toLowerCase();
  return context.includes('leak');
}

export function isFaceMatchNode(node: ViewerNode | null | undefined): boolean {
  if (!node) return false;
  const raw = node.raw || {};
  const metadata = raw.metadata || {};
  if (metadata.isFaceMatch) return true;
  if (Array.isArray(metadata.contexts) && metadata.contexts.length) return true;
  if (Array.isArray(metadata.matches) && metadata.matches.length) return true;
  if (Array.isArray(metadata.results) && metadata.results.length) return true;
  const type = String(raw.type || node.type || '').toLowerCase();
  if (type === 'face_result' && Number(metadata.acceptedMatchCount || 0) > 0) {
    return true;
  }
  if (
    (type === 'image' || type === 'face_result') &&
    (metadata.matchProbability != null || metadata.match_probability != null)
  ) {
    return true;
  }
  return false;
}

/** Pick the best Face1 match payload from node metadata (face_result or image). */
function resolvePrimaryFaceMatch(metadata: any): any | null {
  const matches = Array.isArray(metadata?.matches)
    ? metadata.matches
    : Array.isArray(metadata?.results)
      ? metadata.results
      : [];
  if (matches.length) {
    return (
      matches.slice().sort((left: any, right: any) => {
        const leftP = Number(left?.match_probability ?? left?.matchProbability ?? 0);
        const rightP = Number(right?.match_probability ?? right?.matchProbability ?? 0);
        return rightP - leftP;
      })[0] || null
    );
  }
  if (Array.isArray(metadata?.contexts) && metadata.contexts.length) {
    return {
      contexts: metadata.contexts,
      match_probability: metadata.matchProbability ?? metadata.match_probability,
      identity: metadata.identity,
      image_url: metadata.matchImageUrl || metadata.imageUrl,
      display_image_url: metadata.matchImageUrl || metadata.imageUrl,
    };
  }
  return null;
}

/** Normalize match probability to 0–1 range. */
export function normalizeFaceMatchProbability(value: unknown): number | null {
  const num = Number(value);
  if (!Number.isFinite(num) || num < 0) return null;
  return num > 1 ? num / 100 : num;
}

const FACE_CONTEXT_PREFERRED_KEYS = [
  'app_name',
  'name',
  'phone_number',
  'phone',
  'user_id',
  'uid',
  'username',
  'email',
  'profile_url',
  'url',
];

function formatFaceContextValue(value: unknown): string {
  if (value == null) return '';
  if (Array.isArray(value)) {
    return value
      .map((item) => formatFaceContextValue(item))
      .filter(Boolean)
      .join(', ');
  }
  if (typeof value === 'object') {
    const nested = (value as { value?: unknown }).value;
    if (nested !== undefined) return formatFaceContextValue(nested);
    return '';
  }
  return String(value).trim();
}

function collectFaceContextFieldsFromContexts(contexts: any[], metadata: any, node: ViewerNode): NodeDetailField[] {
  const fields: NodeDetailField[] = [];
  const buckets = new Map<string, string>();

  const pushBucket = (key: string, value: unknown) => {
    const text = formatFaceContextValue(value);
    if (!text || text.toLowerCase() === 'null' || text === '-') return;
    const keyL = String(key || '').toLowerCase();
    if (keyL.includes('image') || keyL.includes('logo') || keyL.includes('photo')) return;
    if (!buckets.has(keyL)) buckets.set(keyL, text);
  };

  contexts.forEach((context: any) => {
    if (!context || typeof context !== 'object' || Array.isArray(context)) return;
    Object.entries(context).forEach(([key, value]) => pushBucket(key, value));
  });

  // Fallback when older graphs lack contexts payload
  if (!buckets.size) {
    pushBucket('app_name', metadata?.sourcePlatform || metadata?.sourceName);
    const identityLabel = metadata?.identity;
    // Face1 identity hashes are not display names
    if (identityLabel && !/^[a-f0-9]{40,}$/i.test(String(identityLabel))) {
      pushBucket('name', identityLabel);
    } else if (node.label && !/high-probability face match/i.test(node.label)) {
      pushBucket('name', node.label);
    }
    pushBucket('phone_number', metadata?.relatedPhones?.[0] || metadata?.phone);
    pushBucket('user_id', metadata?.userId || metadata?.uid);
    pushBucket('username', metadata?.usernames?.[0] || metadata?.handle);
    pushBucket('email', metadata?.relatedEmails?.[0]);
  }

  const orderedKeys = [
    ...FACE_CONTEXT_PREFERRED_KEYS.filter((key) => buckets.has(key)),
    ...[...buckets.keys()].filter((key) => !FACE_CONTEXT_PREFERRED_KEYS.includes(key)).sort(),
  ];

  orderedKeys.forEach((key) => {
    const value = buckets.get(key);
    if (!value) return;
    const isUrl = /^https?:\/\//i.test(value);
    pushField(fields, humanizeKey(key), value, {
      kind: isUrl ? 'link' : 'text',
      href: isUrl ? value : undefined,
    }, key);
  });

  return fields;
}

function collectFaceContextFields(metadata: any, node: ViewerNode): NodeDetailField[] {
  const primary = resolvePrimaryFaceMatch(metadata);
  const contexts = Array.isArray(primary?.contexts)
    ? primary.contexts
    : Array.isArray(metadata?.contexts)
      ? metadata.contexts
      : [];
  return collectFaceContextFieldsFromContexts(contexts, metadata, node);
}

/** Prefer Face1 match URLs, then node image, then query/source image. */
function collectFaceImageCandidates(
  metadata: any,
  node: ViewerNode,
  panel: NodeDetailPanel,
): string[] {
  const primary = resolvePrimaryFaceMatch(metadata);
  const ordered = [
    primary?.display_image_url,
    primary?.image_url,
    metadata?.matchImageUrl,
    metadata?.display_image_url,
    node.image,
    rawImageUrlFromNode(node),
    metadata?.imageUrl,
    ...(Array.isArray(metadata?.matches)
      ? metadata.matches.flatMap((match: any) => [match?.display_image_url, match?.image_url])
      : []),
    ...(Array.isArray(metadata?.results)
      ? metadata.results.flatMap((match: any) => [match?.display_image_url, match?.image_url])
      : []),
    metadata?.sourceImageUrl,
    panel.images[0]?.url,
  ];
  const unique: string[] = [];
  ordered.forEach((candidate) => {
    if (typeof candidate !== 'string') return;
    const url = candidate.trim();
    if (!/^https?:\/\//i.test(url) && !isOsintImageProxyUrl(url) && !url.startsWith('data:image/')) {
      return;
    }
    if (!unique.includes(url)) unique.push(url);
  });
  return unique;
}

function rawImageUrlFromNode(node: ViewerNode): string | null {
  const raw = node.raw || {};
  const url = raw.imageUrl || raw.image_url;
  return typeof url === 'string' ? url : null;
}

function resolveLeakRecord(metadata: any, raw: any): any | null {
  const candidate =
    metadata?.leakData ||
    (raw?.type === 'leak_source' ? metadata?.sourceData : null) ||
    (metadata?.sourceData?.leak_data ? metadata.sourceData : null);
  if (!candidate || typeof candidate !== 'object') return null;
  return candidate;
}

/** Build Leak Search–style Data 1 / Data 2 blocks from leak_data rows. */
function collectLeakStyleRecords(leakRecord: any): OsintStyleLeakRecord[] {
  const rows = Array.isArray(leakRecord?.leak_data) ? leakRecord.leak_data : [];
  if (!rows.length) return [];

  const formatLeakValue = (value: unknown): string => {
    if (value == null) return '';
    if (Array.isArray(value)) {
      return value
        .map((item) => formatLeakValue(item))
        .filter(Boolean)
        .join(', ');
    }
    if (typeof value === 'object') {
      const nested = (value as { value?: unknown }).value;
      if (nested !== undefined) return formatLeakValue(nested);
      return '';
    }
    const text = String(value).trim();
    if (!text || text === ',' || text.toUpperCase() === 'NULL' || text.toLowerCase() === 'null') {
      return '';
    }
    return text.replace(/&amp;/gi, '&').replace(/&lt;/gi, '<').replace(/&gt;/gi, '>');
  };

  const isImageKey = (key: string): boolean => {
    const keyL = key.toLowerCase();
    return (
      keyL === 'image_url' ||
      keyL.includes('image') ||
      keyL.includes('logo') ||
      keyL.includes('avatar') ||
      keyL.includes('photo')
    );
  };

  return rows.map((row: any, index: number) => {
    const fields: NodeDetailField[] = [];
    if (!row || typeof row !== 'object') {
      return { label: `Data ${index + 1}`, fields };
    }

    // Match Leak Search keyvalue pipe: alphabetical by key
    Object.keys(row)
      .sort((a, b) => a.localeCompare(b))
      .forEach((key) => {
        if (isImageKey(key)) return;
        const rawValue = row[key];
        if (rawValue == null || rawValue === '') return;

        let display = '';
        if (
          typeof rawValue === 'string' &&
          /[,|;]/.test(rawValue) &&
          (key.toLowerCase().includes('phone') || key.toLowerCase().includes('email'))
        ) {
          display = rawValue
            .split(/[,|;]+/)
            .map((part) => part.trim())
            .filter(Boolean)
            .join(', ');
        } else {
          display = formatLeakValue(rawValue);
        }
        if (!display) return;

        const isUrl = /^https?:\/\//i.test(display);
        pushField(fields, humanizeKey(key), display, {
          kind: isUrl ? 'link' : 'text',
          href: isUrl ? display : undefined,
        }, key);
      });

    return {
      label: `Data ${index + 1}`,
      fields,
    };
  }).filter((record: OsintStyleLeakRecord) => record.fields.length > 0);
}

export function isIdentityHubNode(node: ViewerNode | null | undefined): boolean {
  if (!node) return false;
  const raw = node.raw || {};
  return Boolean(raw.metadata?.isIdentityHub || String(node.id || '').startsWith('identity-hub:'));
}

export function isIdentityHubChildNode(node: ViewerNode | null | undefined): boolean {
  if (!node || isIdentityHubNode(node)) return false;
  return String(node.raw?.metadata?.parentNodeId || '').startsWith('identity-hub:');
}

export function resolveIdentityHubType(node: ViewerNode | null | undefined): string {
  if (!node) return '';
  const raw = node.raw || {};
  return String(raw.metadata?.identityHubType || raw.metadata?.collectionType || '').toLowerCase();
}

export function isPhotoHubCard(
  node?: ViewerNode | null,
  card?: Pick<OsintStyleCard, 'kind' | 'title' | 'placeholderIcon'> | null,
): boolean {
  const title = String(card?.title || node?.label || '')
    .trim()
    .toLowerCase();
  if (title === 'photos' || title === 'photo' || title.startsWith('photos (')) return true;

  const id = String(node?.id || '').toLowerCase();
  if (id.includes('aggregate:photos') || id.includes('collection:image') || id.includes('collection:photo')) {
    return true;
  }
  if (/(^|:)(photo|photos|image)(:|$)/.test(id)) return true;

  const hubType = resolveIdentityHubType(node);
  if (hubType === 'photo' || hubType === 'photos' || hubType === 'image') return true;

  return card?.kind === 'identity-hub' && card.placeholderIcon === 'bi bi-image';
}

export function osintImageDedupeKey(url?: string | null): string {
  const value = String(url || '').trim().toLowerCase();
  if (!value) return '';
  const proxy = value.match(/\/api\/osint\/image\/([^/?#]+)/i)?.[1];
  if (proxy) return proxy;
  const file = value.split('?')[0].split('#')[0].split('/').pop() || '';
  return file || value;
}

export function uniqueOsintPhotoTiles<T extends { src?: string; detectUrl?: string; platform?: string }>(
  images: Array<T | null | undefined>,
): T[] {
  const seen = new Set<string>();
  const unique: T[] = [];
  images.forEach((image) => {
    if (!image?.src) return;
    if (image.src.startsWith('data:image/svg')) return;
    const key =
      osintImageDedupeKey(image.detectUrl) ||
      osintImageDedupeKey(image.src) ||
      String(image.platform || '').trim().toLowerCase();
    if (!key || seen.has(key)) return;
    seen.add(key);
    unique.push(image);
  });
  return unique;
}

export function resolvePlatformCategoryLabel(platform: string): string {
  const key = String(platform || '').toLowerCase().trim();
  if (
    key.includes('youtube') ||
    key.includes('twitch') ||
    key.includes('vimeo') ||
    key.includes('dailymotion')
  ) {
    return 'Video & Streaming';
  }
  if (
    key.includes('telegram') ||
    key.includes('whatsapp') ||
    key.includes('signal') ||
    key.includes('skype') ||
    key.includes('viber') ||
    key.includes('discord')
  ) {
    return 'Messaging';
  }
  if (
    key.includes('spotify') ||
    key.includes('soundcloud') ||
    key.includes('deezer') ||
    key.includes('pandora')
  ) {
    return 'Music';
  }
  if (
    key.includes('reddit') ||
    key.includes('quora') ||
    key.includes('stackoverflow') ||
    key.includes('forum')
  ) {
    return 'Forums & Community';
  }
  if (
    key.includes('linkedin') ||
    key.includes('github') ||
    key.includes('gitlab') ||
    key.includes('indeed') ||
    key.includes('behance') ||
    key.includes('dribbble')
  ) {
    return 'Professional';
  }
  if (
    key.includes('facebook') ||
    key.includes('instagram') ||
    key.includes('twitter') ||
    key === 'x' ||
    key.includes('snapchat') ||
    key.includes('tiktok') ||
    key.includes('pinterest') ||
    key.includes('tumblr')
  ) {
    return 'Social Media';
  }
  return 'Other';
}

function isPictureLikeKey(key: string): boolean {
  const normalized = String(key || '').toLowerCase();
  if (isLogoFieldKey(key)) return false;
  return /picture|photo|image|avatar|thumbnail|banner|cover/.test(normalized);
}

/** Never list profile-photo URL / hash fields as green text — they render in Pictures. */
function shouldOmitOsintCardPictureField(key: string, value?: string): boolean {
  const normalized = String(key || '')
    .toLowerCase()
    .replace(/[\s-]/g, '_');
  if (
    normalized === 'picture' ||
    normalized === 'picture_url' ||
    normalized === 'image_hash' ||
    normalized === 'image_url' ||
    IMAGE_FIELD_KEYS.has(normalized)
  ) {
    return true;
  }
  // Keep EXIF / "Apply Min Photo" text fields; only omit when the value is an image asset.
  if (!isPictureLikeKey(key) || !value || value === 'N/A') return false;
  if (isBase64DataUrl(value)) return true;
  return isLikelyImageUrl(value) || isOsintImageProxyUrl(value);
}

function extractOsintCardFields(sourceData: any): NodeDetailField[] {
  const fields: NodeDetailField[] = [];
  const sourceFields = sourceData?.fields;
  if (!sourceFields || typeof sourceFields !== 'object') return fields;

  collectNormalizedOsintFields(sourceFields).forEach(([key, entry]) => {
    const type = String((entry as any)?.type || '').toLowerCase();
    if (type === 'base64string') return;
    const value = extractSourceFieldValue(entry);
    if (!value || isBase64DataUrl(value)) return;
    if (shouldOmitOsintCardPictureField(key, value)) return;
    const label = toOsintDisplayName(key);
    const isUrl = /^https?:\/\//i.test(value) && value !== 'N/A';
    const taggedSearchType = resolveOsintSearchTypeTag(entry);
    pushField(
      fields,
      label,
      value,
      {
        tone: 'default',
        kind: isUrl ? 'link' : 'text',
        ...(taggedSearchType ? { searchQueryType: taggedSearchType } : {}),
      },
      key,
    );
  });

  return fields;
}

/** Prefer server `osint-search-type` on a source field when tagging detail-card search. */
function resolveOsintSearchTypeTag(entry: any): NodeDetailField['searchQueryType'] | undefined {
  if (!entry || typeof entry !== 'object') return undefined;
  const raw = String(entry['osint-search-type'] ?? entry.osintSearchType ?? '')
    .toLowerCase()
    .trim();
  if (raw === 'phone' || raw === 'email' || raw === 'username' || raw === 'name' || raw === 'image') {
    return raw;
  }
  if (raw === 'face' || raw === 'photo' || raw === 'picture') return 'image';
  return undefined;
}

function extractOsintCardDescription(sourceData: any): string | undefined {
  const sourceFields = sourceData?.fields;
  if (!sourceFields || typeof sourceFields !== 'object') return undefined;
  for (const [key, entry] of Object.entries(sourceFields)) {
    if (!/description|bio|about/i.test(key)) continue;
    const value = extractSourceFieldValue(entry);
    if (value && value !== 'N/A') return value;
  }
  return undefined;
}

function extractProfileUrl(metadata: any, sourceData: any): string | undefined {
  const urls = Array.isArray(metadata?.urls) ? metadata.urls : [];
  const fromMeta = urls.find(
    (url: unknown) =>
      isHttpAssetUrl(url) &&
      !IMAGE_URL_PATTERN.test(String(url)) &&
      !isOsintImageProxyUrl(url),
  );
  if (fromMeta) return String(fromMeta);

  const accounts = Array.isArray(sourceData?.accounts) ? sourceData.accounts : [];
  for (const account of accounts) {
    const url = extractSourceFieldValue(
      account?.profile_url || account?.profileUrl || account?.url || account?.maps_url,
    );
    if (url && isHttpAssetUrl(url) && !isOsintImageProxyUrl(url)) return url;
  }

  const fields = sourceData?.fields;
  if (fields && typeof fields === 'object') {
    for (const key of ['profile_url', 'profileUrl', 'url', 'website', 'link']) {
      const value = extractSourceFieldValue(fields[key]);
      if (
        value &&
        isHttpAssetUrl(value) &&
        !IMAGE_URL_PATTERN.test(value) &&
        !isOsintImageProxyUrl(value)
      ) {
        return value;
      }
    }
  }
  return undefined;
}

function identityPlaceholderIcon(hubType: string, rawType: string): string {
  if (hubType === 'name' || rawType === 'person' || rawType === 'aggregate_names') return 'bi bi-person';
  if (hubType === 'photo' || hubType === 'image' || rawType === 'image' || rawType === 'aggregate_photos') {
    return 'bi bi-image';
  }
  if (hubType === 'email_hint' || rawType === 'email_hint') return 'bi bi-at';
  if (hubType === 'email' || rawType === 'email' || rawType === 'aggregate_emails') return 'bi bi-envelope';
  if (hubType === 'phone' || rawType === 'phone' || rawType === 'aggregate_phones') return 'bi bi-telephone';
  return 'bi bi-diagram-3';
}

function resolveAggregateContactKind(
  node: ViewerNode,
  rawType: string,
  metadata: Record<string, any>,
): 'phone' | 'email' | 'name' | 'photo' | null {
  const collectionType = String(metadata['collectionType'] || '').toLowerCase();
  const id = String(node.id || '').toLowerCase();
  const type = String(rawType || node.raw?.type || node.type || '').toLowerCase();
  if (
    collectionType === 'phone' ||
    type === 'aggregate_phones' ||
    id.includes('aggregate:phones') ||
    id.includes('collection:phones')
  ) {
    return 'phone';
  }
  if (
    collectionType === 'email' ||
    type === 'aggregate_emails' ||
    id.includes('aggregate:emails') ||
    id.includes('collection:emails')
  ) {
    return 'email';
  }
  if (
    collectionType === 'name' ||
    type === 'aggregate_names' ||
    id.includes('aggregate:names') ||
    id.includes('collection:names')
  ) {
    return 'name';
  }
  if (
    collectionType === 'photo' ||
    collectionType === 'image' ||
    type === 'aggregate_photos' ||
    id.includes('aggregate:photos') ||
    id.includes('collection:image')
  ) {
    return 'photo';
  }
  return null;
}

function buildAggregateContactFields(
  items: Array<{
    value: string;
    sources: string[];
    sourceNodeId?: string;
    sourceLabel?: string;
  }>,
  kind: 'phone' | 'email' | 'name' | 'photo',
): NodeDetailField[] {
  const noun =
    kind === 'phone' ? 'Phone' : kind === 'email' ? 'Email' : kind === 'name' ? 'Name' : 'Photo';
  const valueType: NodeDetailField['preselectValueType'] =
    kind === 'photo' ? 'image' : kind === 'name' ? 'name' : kind;
  return items.map((item, index) => {
    const sources = (item.sources || []).map((src) => String(src).trim()).filter(Boolean);
    const sourceLabel =
      sources.length > 0
        ? sources.join(', ')
        : String(item.sourceLabel || '').trim() || undefined;
    const jumpToNodeId = String(item.sourceNodeId || '').trim();
    return {
      label: items.length === 1 ? noun : `${noun} ${index + 1}`,
      value: item.value,
      kind: 'text' as const,
      subValue: sourceLabel,
      // R12: aggregates are view-only — jump to the owning source, do not search here.
      ...(jumpToNodeId
        ? { jumpToNodeId, preselectValueType: valueType }
        : {}),
    };
  });
}

function relatedItemImageUrl(item: ViewerNode): string | undefined {
  const metadata = item.raw?.metadata || {};
  const candidates = [
    item.image,
    item.raw?.imageUrl,
    metadata.relatedImageUrl,
    metadata.imageUrl,
    metadata.queryValue,
  ];
  return candidates.find((value) => isHttpAssetUrl(value)) as string | undefined;
}

function buildRelatedIdentityItems(node: ViewerNode, graphData: ViewerGraphData): OsintStyleRelatedItem[] {
  const hubId = node.id;
  const childIds = new Set<string>(
    Array.isArray(node.raw?.metadata?.hubChildIds) ? node.raw.metadata.hubChildIds : [],
  );
  return graphData.nodes
    .filter((item) => item.id !== hubId && (childIds.has(item.id) || item.raw?.metadata?.parentNodeId === hubId))
    .map((item) => ({
      id: item.id,
      label: item.label,
      image: relatedItemImageUrl(item),
      subtitle: Array.isArray(item.raw?.metadata?.relatedPlatforms)
        ? item.raw.metadata.relatedPlatforms.slice(0, 2).join(', ')
        : item.raw?.metadata?.sourceName || item.raw?.metadata?.platform || undefined,
    }))
    .sort((left, right) => left.label.localeCompare(right.label));
}

export function buildOsintStyleCard(node: ViewerNode, graphData: ViewerGraphData): OsintStyleCard {
  const panel = buildNodeDetailPanel(node, graphData);
  const raw = node.raw || {};
  const metadata = raw.metadata || {};
  const rawType = String(raw.type || node.type || '').toLowerCase();
  const hubType = resolveIdentityHubType(node);
  const platform = String(metadata.platform || metadata.sourceName || '').trim();
  const profileUrl = extractProfileUrl(metadata, metadata.sourceData);

  // Leak sources: Leak Search–style Data 1 / Data 2 blocks, no pictures frame.
  if (isLeakSourceNode(node)) {
    const leakRecord = resolveLeakRecord(metadata, raw);
    const leakRecords = collectLeakStyleRecords(leakRecord || {});
    const metaFields: NodeDetailField[] = [];
    const leakDate = leakRecord?.leak_date || metadata.leakDate;
    if (leakDate) {
      pushField(metaFields, 'Leak date', leakDate);
    }

    return {
      kind: 'leak',
      title: platform || node.label || 'Leak source',
      category: 'Leak',
      found: true,
      heroImage: null,
      placeholderIcon: 'bi bi-shield-exclamation',
      accent: '#ef5350',
      fields: metaFields,
      leakRecords,
      description: String(leakRecord?.description || metadata.description || '').trim() || undefined,
      relatedItems: [],
    };
  }

  // Face matches: Face Search–style probability bar + large image + context fields.
  if (isFaceMatchNode(node)) {
    const primary = resolvePrimaryFaceMatch(metadata);
    const matchProbability = normalizeFaceMatchProbability(
      primary?.match_probability ??
      primary?.matchProbability ??
      metadata.matchProbability ??
      metadata.match_probability ??
      node.confidence,
    );
    const faceContexts = collectFaceContextFields(metadata, node);
    const faceImageCandidates = collectFaceImageCandidates(metadata, node, panel);
    const heroImage = faceImageCandidates.length
      ? { url: faceImageCandidates[0], label: node.label || 'Face match' }
      : null;
    const nameFromContext = faceContexts.find((field) => /^name$/i.test(field.label))?.value;
    const appName =
      faceContexts.find((field) => /app name/i.test(field.label))?.value ||
      metadata.sourcePlatform ||
      metadata.sourceName ||
      'Face match';

    return {
      kind: 'face',
      title: String(nameFromContext || appName || node.label || 'Face match'),
      category: 'Face match',
      found: true,
      heroImage,
      placeholderIcon: 'bi bi-person-bounding-box',
      accent: '#45f7a9',
      fields: [],
      faceContexts,
      faceImageCandidates,
      matchProbability,
      relatedItems: [],
    };
  }

  const heroImage =
    panel.images[0] ||
    (node.image ? { url: node.image, label: node.label } : null) ||
    null;

  if (isIdentityHubNode(node)) {
    const relatedItems = buildRelatedIdentityItems(node, graphData);
    const count = relatedItems.length || Number(metadata.searchableCount || 0);
    const relatedHero = relatedItems.find((item) => item.image);
    return {
      kind: 'identity-hub',
      title: node.label,
      category: count ? `${count} recovered` : 'Identity',
      found: count > 0,
      heroImage: heroImage || (relatedHero?.image ? { url: relatedHero.image, label: relatedHero.label } : null),
      placeholderIcon: identityPlaceholderIcon(hubType, rawType),
      accent: IDENTITY_HUB_ACCENTS[hubType === 'image' ? 'photo' : hubType] || ENTITY_COLORS[node.type] || '#64748b',
      fields: [],
      relatedItems,
      isPhotoHub: isPhotoHubCard(node, {
        kind: 'identity-hub',
        title: node.label,
        placeholderIcon: identityPlaceholderIcon(hubType, rawType),
      }),
    };
  }

  if (isAggregateHubNode({ id: node.id, type: raw.type, metadata })) {
    const contactKind = resolveAggregateContactKind(node, rawType, metadata);
    if (contactKind) {
      const items = getAggregateItems({ metadata });
      const fields = buildAggregateContactFields(items, contactKind);
      const count = Number(metadata.uniqueCount) || items.length;
      return {
        kind: 'identity',
        title: node.label,
        category:
          contactKind === 'phone'
            ? count === 1
              ? '1 phone number'
              : `${count} phone numbers`
            : contactKind === 'email'
              ? count === 1
                ? '1 email'
                : `${count} emails`
              : contactKind === 'name'
                ? count === 1
                  ? '1 name'
                  : `${count} names`
                : count === 1
                  ? '1 photo'
                  : `${count} photos`,
        found: count > 0,
        heroImage: null,
        placeholderIcon:
          contactKind === 'phone'
            ? 'bi bi-telephone'
            : contactKind === 'email'
              ? 'bi bi-envelope'
              : contactKind === 'name'
                ? 'bi bi-person'
                : 'bi bi-image',
        accent:
          ENTITY_COLORS[
            contactKind === 'name' ? 'person' : contactKind === 'photo' ? 'image' : contactKind
          ],
        fields: contactKind === 'photo'
          ? []
          : fields.length
            ? fields
            : [
                {
                  label:
                    contactKind === 'phone'
                      ? 'Phones'
                      : contactKind === 'email'
                        ? 'Emails'
                        : 'Names',
                  value: 'None found',
                },
              ],
        relatedItems:
          contactKind === 'photo'
            ? items
                .filter((item) => {
                  const url = String(item.value || '').trim();
                  return /^https?:\/\//i.test(url) || isOsintImageProxyUrl(url) || url.startsWith('data:image/');
                })
                .map((item, index) => ({
                  id: item.sourceNodeId || `${node.id}:photo:${index}`,
                  label: item.sourceLabel || item.sources?.[0] || `Photo ${index + 1}`,
                  image: item.value,
                  subtitle: item.sourceLabel || (item.sources?.length ? item.sources.join(', ') : undefined),
                }))
            : [],
        isPhotoHub: contactKind === 'photo',
      };
    }
  }

  if (rawType === 'face_image') {
    const imageUrl =
      heroImage?.url ||
      String(metadata.queryValue || metadata.imageUrl || metadata.canonicalImage || '').trim() ||
      (Array.isArray(metadata.searchableValues?.image) ? metadata.searchableValues.image[0] : '') ||
      (Array.isArray(metadata.searchable?.image) ? metadata.searchable.image[0] : '');
    const faceUrl = String(imageUrl || '').trim();
    const hasFaceUrl =
      Boolean(faceUrl) &&
      (/^https?:\/\//i.test(faceUrl) || isOsintImageProxyUrl(faceUrl) || faceUrl.startsWith('data:image/'));
    return {
      kind: 'face',
      title: node.label || 'Face image',
      category: 'Face image',
      found: hasFaceUrl,
      heroImage: hasFaceUrl ? { url: faceUrl, label: node.label || 'Face' } : null,
      placeholderIcon: 'bi bi-person-bounding-box',
      accent: '#5b8fb9',
      fields: [],
      faceContexts: [
        ...(metadata.parentNodeId
          ? [
              {
                label: 'Source',
                value: resolveNodeLabel(
                  new Map(graphData.nodes.map((item) => [item.id, item])),
                  metadata.parentNodeId,
                ) || String(metadata.parentNodeId),
              },
            ]
          : []),
      ],
      relatedItems: [],
    };
  }

  if (rawType === 'query_root' || rawType === 'image_query') {
    const queryType = String(metadata.queryType || '').toLowerCase();
    const queryValue = String(metadata.queryValue || node.label || '').trim();
    const fields: NodeDetailField[] = [];
    pushField(fields, queryType === 'email' ? 'Email' : queryType === 'username' ? 'Username' : queryType === 'image' ? 'Image' : 'Phone', queryValue);
    pushField(fields, 'Layer', metadata.layer != null ? String(metadata.layer) : '0');
    return {
      kind: 'identity',
      title: queryValue || node.label,
      category: rawType === 'query_root' ? 'Investigation root' : 'Face query',
      found: true,
      heroImage:
        queryType === 'image' &&
        (/^https?:\/\//i.test(queryValue) || isOsintImageProxyUrl(queryValue) || queryValue.startsWith('data:image/'))
          ? { url: queryValue, label: 'Query' }
          : heroImage,
      placeholderIcon:
        queryType === 'email'
          ? 'bi bi-envelope'
          : queryType === 'username'
            ? 'bi bi-at'
            : queryType === 'image'
              ? 'bi bi-image'
              : 'bi bi-telephone',
      accent: ENTITY_COLORS[queryType === 'email' ? 'email' : queryType === 'image' ? 'image' : 'phone'],
      fields,
      relatedItems: [],
    };
  }

  if (
    rawType === 'osint_source' ||
    rawType === 'social_profile' ||
    (metadata.sourceData && !metadata.leakData && !Array.isArray(metadata.sourceData?.leak_data))
  ) {
    const cardFields = collectOsintPlatformCardFields(metadata.sourceData);
    return {
      kind: 'platform',
      title: platform || node.label,
      category: resolvePlatformCategoryLabel(platform || node.label),
      found: metadata.hasAccount !== false,
      profileUrl,
      heroImage,
      placeholderIcon: 'bi bi-globe2',
      accent: '#38bdf8',
      fields: cardFields,
      description: extractOsintCardDescription(metadata.sourceData),
      relatedItems: [],
    };
  }

  const identityFields: NodeDetailField[] = [];
  if (rawType === 'person' || hubType === 'name') {
    pushField(identityFields, 'Name', metadata.queryValue || node.label);
  } else if (rawType === 'email_hint' || metadata.isHint) {
    pushField(identityFields, 'Email hint', metadata.queryValue || node.label);
  } else if (rawType === 'email') {
    pushField(identityFields, 'Email', metadata.queryValue || node.label, { kind: 'text' });
  } else if (rawType === 'image') {
    pushField(identityFields, 'Photo', metadata.sourceName || node.label);
  } else {
    pushField(identityFields, 'Value', metadata.queryValue || node.label);
  }
  pushField(identityFields, 'Sources', metadata.relatedPlatforms, { kind: 'chips' });
  pushField(identityFields, 'Related names', metadata.relatedNames, { kind: 'chips' });
  if (profileUrl) {
    pushField(identityFields, 'Profile URL', profileUrl, { kind: 'link', href: profileUrl });
  }

  const identityKind =
    rawType === 'person' ||
      rawType === 'email' ||
      rawType === 'email_hint' ||
      rawType === 'image' ||
      metadata.isHint
      ? 'identity'
      : 'generic';

  return {
    kind: identityKind,
    title: node.label,
    category:
      rawType === 'email_hint' || metadata.isHint
        ? 'Email Hint'
        : rawType === 'image'
          ? 'Photo'
          : entityTypeLabel(node.type),
    found: true,
    profileUrl,
    heroImage,
    placeholderIcon: identityPlaceholderIcon(hubType || rawType, rawType),
    accent:
      IDENTITY_HUB_ACCENTS[rawType === 'email_hint' ? 'email_hint' : rawType === 'image' ? 'photo' : rawType] ||
      ENTITY_COLORS[node.type] ||
      '#64748b',
    fields: identityFields,
    relatedItems: [],
  };
}
